// Subjects.js
import { useEffect, useState } from "react";
import {
  addSubject,
  getSubjects,
  deleteSubject,
  updateSubject,
} from "../services/firebaseService";

export default function Subjects() {
  const [subjectName, setSubjectName] = useState("");
  const [subjectCode, setSubjectCode] = useState("");
  const [status, setStatus] = useState("Active");
  const [subjects, setSubjects] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  // Load Subjects
  useEffect(() => {
    getSubjects(setSubjects);
  }, []);

  // Save or Update Subject
  const handleSave = async () => {
    if (!subjectName.trim()) {
      alert("Please enter Subject Name");
      return;
    }

    try {
      if (isEditing && editingId) {
        // Update existing subject
        await updateSubject(editingId, {
          name: subjectName.trim(),
          code: subjectCode.trim().toUpperCase(),
          status,
        });
        setIsEditing(false);
        setEditingId(null);
      } else {
        // Add new subject
        await addSubject({
          name: subjectName.trim(),
          code: subjectCode.trim().toUpperCase(),
          status,
        });
      }

      setSubjectName("");
      setSubjectCode("");
      setStatus("Active");
    } catch (error) {
      console.error(error);
      alert("Failed to save subject.");
    }
  };

  // Delete Subject
  const handleDelete = async (id) => {
    if (window.confirm("Delete this subject?")) {
      await deleteSubject(id);
    }
  };

  // Edit Subject
  const handleEdit = (subject) => {
    setSubjectName(subject.name);
    setSubjectCode(subject.code || "");
    setStatus(subject.status || "Active");
    setEditingId(subject.id);
    setIsEditing(true);
    // Scroll to form
    document.querySelector(".form-container")?.scrollIntoView({ behavior: "smooth" });
  };

  // Cancel Edit
  const handleCancel = () => {
    setSubjectName("");
    setSubjectCode("");
    setStatus("Active");
    setEditingId(null);
    setIsEditing(false);
  };

  return (
    <div
      style={{
        maxWidth: "700px",
        margin: "20px auto",
        padding: "15px",
      }}
    >
      <h2
        style={{
          textAlign: "center",
          marginBottom: "20px",
        }}
      >
        📚 Subject Management
      </h2>

      {/* Subject Form */}
      <div
        className="form-container"
        style={{
          background: "#fff",
          padding: "20px",
          borderRadius: "10px",
          boxShadow: "0 2px 10px rgba(0,0,0,.1)",
          marginBottom: "20px",
        }}
      >
        <div style={{ marginBottom: "15px" }}>
          <label>Subject Name *</label>

          <input
            type="text"
            placeholder="Enter Subject Name"
            value={subjectName}
            onChange={(e) => setSubjectName(e.target.value)}
            style={{
              width: "100%",
              padding: "12px",
              marginTop: "5px",
              borderRadius: "8px",
              border: "1px solid #ccc",
              boxSizing: "border-box",
            }}
          />
        </div>

        <div style={{ marginBottom: "15px" }}>
          <label>Subject Code</label>

          <input
            type="text"
            placeholder="Example : MATH"
            value={subjectCode}
            onChange={(e) => setSubjectCode(e.target.value.toUpperCase())}
            style={{
              width: "100%",
              padding: "12px",
              marginTop: "5px",
              borderRadius: "8px",
              border: "1px solid #ccc",
              boxSizing: "border-box",
            }}
          />
        </div>

        <div style={{ marginBottom: "20px" }}>
          <label>Status</label>

          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            style={{
              width: "100%",
              padding: "12px",
              marginTop: "5px",
              borderRadius: "8px",
              border: "1px solid #ccc",
              boxSizing: "border-box",
            }}
          >
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>

        <div style={{ display: "flex", gap: "10px" }}>
          <button
            onClick={handleSave}
            style={{
              flex: 1,
              padding: "14px",
              background: isEditing ? "#f59e0b" : "#2563eb",
              color: "#fff",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
              fontSize: "16px",
              fontWeight: "bold",
            }}
          >
            {isEditing ? "✏️ Update Subject" : "💾 Save Subject"}
          </button>

          {isEditing && (
            <button
              onClick={handleCancel}
              style={{
                padding: "14px 24px",
                background: "#6b7280",
                color: "#fff",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                fontSize: "16px",
                fontWeight: "bold",
              }}
            >
              Cancel
            </button>
          )}
        </div>

        {isEditing && (
          <p style={{ marginTop: 12, color: "#f59e0b", fontSize: "0.9rem" }}>
            ✏️ Editing: {subjectName}
          </p>
        )}
      </div>

      {/* Subject List */}
      <div
        style={{
          background: "#fff",
          borderRadius: "10px",
          padding: "15px",
          boxShadow: "0 2px 10px rgba(0,0,0,.1)",
        }}
      >
        <h3>All Subjects ({subjects.length})</h3>

        {subjects.length === 0 ? (
          <p>No Subjects Added.</p>
        ) : (
          subjects.map((subject) => (
            <div
              key={subject.id}
              style={{
                border: "1px solid #ddd",
                borderRadius: "10px",
                padding: "15px",
                marginTop: "15px",
                background: "#fafafa",
              }}
            >
              <h3 style={{ margin: "0 0 8px 0" }}>{subject.name}</h3>

              <p style={{ margin: "4px 0" }}>
                <strong>Code:</strong> {subject.code || "-"}
              </p>

              <p style={{ margin: "4px 0 12px 0" }}>
                <strong>Status:</strong>{" "}
                <span
                  style={{
                    color: subject.status === "Active" ? "#16a34a" : "#dc2626",
                    fontWeight: "500",
                  }}
                >
                  {subject.status}
                </span>
              </p>

              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  flexWrap: "wrap",
                }}
              >
                <button
                  onClick={() => handleEdit(subject)}
                  style={{
                    background: "#f59e0b",
                    color: "#fff",
                    border: "none",
                    padding: "10px 20px",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontWeight: "500",
                    flex: "1",
                    minWidth: "80px",
                  }}
                >
                  ✏️ Edit
                </button>

                <button
                  onClick={() => handleDelete(subject.id)}
                  style={{
                    background: "#dc2626",
                    color: "#fff",
                    border: "none",
                    padding: "10px 20px",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontWeight: "500",
                    flex: "1",
                    minWidth: "80px",
                  }}
                >
                  🗑 Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}